import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Layout,
  Text,
  Button,
  BlockStack,
  Spinner,
  Banner,
  Badge,
  DataTable,
  Pagination,
  Modal,
  LegacyCard,
  TextField,
  Icon,
} from '@shopify/polaris';
import { ExportMinor, InfoMinor, SearchMinor } from '@shopify/polaris-icons';
import '@shopify/polaris/build/esm/styles.css';

// Import services
import { printingsService } from '../services/printingsService';
import { ordersService } from '../services/ordersService';

const PrintingsPage = ({ printingProfile = 'all' }) => {
  const navigate = useNavigate();
  const isAll = printingProfile === 'all';
  const isPremium = printingProfile === 'premium';
  const isBooklet = printingProfile === 'booklet';
  const verificationLane = isBooklet ? 'booklet' : (isPremium ? 'hard_cover' : null);
  const profileLabel = isBooklet
    ? 'Booklet'
    : (isPremium ? '18.5 x 18.5 Hard Cover' : (isAll ? 'All' : 'Standard'));
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isFetchingBosta, setIsFetchingBosta] = useState(false);
  const [bostaError, setBostaError] = useState(null);
  const pollingInterval = useRef(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(10);

  // Modal state
  const [activeModal, setActiveModal] = useState(false);
  const [modalOrderReferences, setModalOrderReferences] = useState([]);
  const [modalGenerationReferences, setModalGenerationReferences] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [activeShippingProvider, setActiveShippingProvider] = useState('bosta');
  const historyRequestId = useRef(0);

  const loadHistory = useCallback(async (page = 1) => {
    const requestId = ++historyRequestId.current;
    try {
      setIsLoadingHistory(true);
      const response = await printingsService.getAllPrintings(
        page,
        itemsPerPage,
        debouncedSearchQuery,
        printingProfile
      );
      if (requestId === historyRequestId.current && response.success) {
        setHistory(response.data || []);
        if (response.pagination) {
          setTotalPages(response.pagination.pageCount || 1);
          setTotalItems(response.pagination.total || 0);
        }
      }
    } catch (err) {
      console.error('Error loading history:', err);
    } finally {
      if (requestId === historyRequestId.current) setIsLoadingHistory(false);
    }
  }, [debouncedSearchQuery, itemsPerPage, printingProfile]);

  const pollJobStatus = useCallback(async (jobId) => {
    try {
      const response = await printingsService.getPrintingById(jobId);
      if (response.success) {
        setCurrentJob(response.data);

        if (response.data.status === 'completed' || response.data.status === 'failed') {
          if (pollingInterval.current) {
            clearInterval(pollingInterval.current);
          }
          setIsGenerating(false);
          loadHistory(currentPage);
        }
      }
    } catch (err) {
      console.error('Error polling job status:', err);
    }
  }, [currentPage, loadHistory]);

  // Handle search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, printingProfile]);

  useEffect(() => {
    setCurrentJob(null);
    setIsGenerating(false);
    setError(null);
    if (pollingInterval.current) clearInterval(pollingInterval.current);
  }, [printingProfile]);

  // Load printing history on mount and when page changes
  useEffect(() => {
    loadHistory(currentPage);
  }, [currentPage, loadHistory]);

  useEffect(() => {
    const loadShippingProviderSetting = async () => {
      try {
        const settings = await ordersService.getAdminSettings();
        setActiveShippingProvider(settings?.shipping?.provider || 'bosta');
      } catch (providerError) {
        console.error('Error loading shipping provider setting:', providerError);
        setActiveShippingProvider('bosta');
      }
    };

    loadShippingProviderSetting();
  }, []);

  // Listen for refresh event from parent Page actions
  useEffect(() => {
    const handleRefresh = () => {
      loadHistory(currentPage);
    };
    const handleVerifyScan = () => {
      if (!verificationLane) return;
      navigate(`/printings/verify?lane=${verificationLane}`);
    };

    window.addEventListener('printingsRefresh', handleRefresh);
    window.addEventListener('printingsVerifyScan', handleVerifyScan);
    return () => {
      window.removeEventListener('printingsRefresh', handleRefresh);
      window.removeEventListener('printingsVerifyScan', handleVerifyScan);
    };
  }, [currentPage, loadHistory, navigate, verificationLane]);

  // Poll for job status when a job is active
  useEffect(() => {
    if (currentJob && (currentJob.status === 'pending' || currentJob.status === 'processing')) {
      // Start polling
      pollingInterval.current = setInterval(() => {
        pollJobStatus(currentJob._id);
      }, 3000); // Poll every 3 seconds

      return () => {
        if (pollingInterval.current) {
          clearInterval(pollingInterval.current);
        }
      };
    }
  }, [currentJob, pollJobStatus]);

  const handleGeneratePrintings = async () => {
    setIsGenerating(true);
    setError(null);
    setCurrentJob(null);

    try {
      const response = await printingsService.generatePrintings(null, printingProfile);

      if (response.success) {
        setCurrentJob({
          _id: response.data.printingId,
          status: response.data.status,
          date: response.data.date,
          printingProfile: response.data.printingProfile || printingProfile,
          totalGenerations: response.data.totalGenerations,
        });
      } else {
        setError(response.error || 'Failed to generate printings');
        setIsGenerating(false);
      }
    } catch (err) {
      setError(err.message || 'An error occurred while generating printings');
      setIsGenerating(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleOpenModal = (job) => {
    setModalOrderReferences(job.orderReferences || []);
    setModalGenerationReferences(job.generationReferences || []);
    setActiveModal(true);
  };

  const handleCloseModal = () => {
    setActiveModal(false);
    setModalOrderReferences([]);
    setModalGenerationReferences([]);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { status: 'info', label: 'Pending' },
      processing: { status: 'attention', label: 'Processing' },
      completed: { status: 'success', label: 'Completed' },
      failed: { status: 'critical', label: 'Failed' },
    };
    const config = statusMap[status] || { status: 'default', label: status };
    return <Badge status={config.status}>{config.label}</Badge>;
  };

  const handleFetchBostaShippingSlip = async (printingId) => {
    setIsFetchingBosta(true);
    setBostaError(null);

    try {
      const response = await printingsService.fetchBostaAll(printingId, {
        requestedAwbType: 'A4',
        lang: 'ar'
      });

      if (response.success) {
        // Update current job if it's the same one
        if (currentJob && currentJob._id === printingId) {
          const updatedJob = await printingsService.getPrintingById(printingId);
          if (updatedJob.success) {
            setCurrentJob(updatedJob.data);
          }
        }
        // Reload history to show updated data
        loadHistory(currentPage);
      } else {
        setBostaError(response.error || 'Failed to fetch shipping slip');
      }
    } catch (err) {
      setBostaError(err.message || 'An error occurred while fetching shipping data');
    } finally {
      setIsFetchingBosta(false);
    }
  };

  const isShippingCompanyProvider = activeShippingProvider === 'shipping_company';
  const shippingProviderLabel = isShippingCompanyProvider ? 'Shipping Company' : 'Bosta';
  const shippingSlipLabel = isShippingCompanyProvider ? 'Shipping Labels' : 'Shipping Slip';

  return (
    <>
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">
                {isAll ? 'Generate Printings' : `Generate ${profileLabel} Printings`}
              </Text>

              <Button
                variant="primary"
                onClick={handleGeneratePrintings}
                loading={isGenerating}
                disabled={isGenerating}
                icon={ExportMinor}
              >
                {isAll ? 'Generate Printings' : `Generate ${profileLabel} Printings`}
              </Button>

              {error && (
                <Banner status="critical">
                  <p>{error}</p>
                </Banner>
              )}

              {currentJob && (
                <div style={{
                  padding: '20px',
                  background: '#f6f6f7',
                  borderRadius: '8px',
                  border: '1px solid #e3e3e3',
                }}>
                  <BlockStack gap="300">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Text variant="headingMd" as="h3">
                        Current Job Status
                      </Text>
                      {getStatusBadge(currentJob.status)}
                      {isPremium && <Badge status="attention">Premium</Badge>}
                      {isBooklet && <Badge status="info">Booklet</Badge>}
                          {isAll && <Badge status="info">All regular types</Badge>}
                    </div>

                    {(currentJob.status === 'pending' || currentJob.status === 'processing') && (
                      <div style={{
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                        <Spinner size="large" />
                        <Text variant="bodyMd" tone="subdued">
                          {currentJob.status === 'pending'
                            ? 'Job queued, waiting to start...'
                            : 'Generating printings... This may take a few minutes.'}
                        </Text>
                        {currentJob.status === 'processing' && currentJob.processingStage && (
                          <Text variant="bodySm" tone="subdued">
                            {currentJob.processingStage === 'generating_booklet_pdf'
                              ? `Generating booklet ${currentJob.processingGenerationNumber || 1} of ${currentJob.totalGenerations || 1}${currentJob.processingGenerationId ? ` (${currentJob.processingGenerationId})` : ''}`
                              : currentJob.processingStage === 'uploading_booklet_zip'
                                ? 'Uploading booklet ZIP...'
                                : 'Preparing printing files...'}
                          </Text>
                        )}
                      </div>
                    )}

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: '16px',
                      marginTop: '12px',
                    }}>
                      <div style={{
                        padding: '16px',
                        background: 'white',
                        borderRadius: '6px',
                      }}>
                        <Text variant="bodySm" tone="subdued">Date</Text>
                        <Text variant="headingLg" as="p">
                          {formatDate(currentJob.date)}
                        </Text>
                      </div>

                      <div style={{
                        padding: '16px',
                        background: 'white',
                        borderRadius: '6px',
                      }}>
                        <Text variant="bodySm" tone="subdued">Total Generations</Text>
                        <Text variant="headingLg" as="p">
                          {currentJob.totalGenerations || 0}
                        </Text>
                      </div>

                      {currentJob.status === 'completed' && (
                        <>
                          {isAll ? (
                            <>
                              <div style={{
                                padding: '16px',
                                background: 'white',
                                borderRadius: '6px',
                              }}>
                                <Text variant="bodySm" tone="subdued">Booklet PDFs</Text>
                                <Text variant="headingLg" as="p">
                                  {currentJob.totalBookletPdfs || 0}
                                </Text>
                              </div>
                              <div style={{
                                padding: '16px',
                                background: 'white',
                                borderRadius: '6px',
                              }}>
                                <Text variant="bodySm" tone="subdued">Hard-cover Generations</Text>
                                <Text variant="headingLg" as="p">
                                  {currentJob.totalHardCoverGenerations || 0}
                                </Text>
                              </div>
                            </>
                          ) : isBooklet ? (
                            <div style={{
                              padding: '16px',
                              background: 'white',
                              borderRadius: '6px',
                            }}>
                              <Text variant="bodySm" tone="subdued">Booklet PDFs</Text>
                              <Text variant="headingLg" as="p">
                                {currentJob.totalBookletPdfs || 0}
                              </Text>
                              <Text variant="bodySm" tone="subdued">
                                {currentJob.bookletUsedCached || 0} cached, {currentJob.bookletGeneratedNew || 0} new
                              </Text>
                            </div>
                          ) : (
                            <>
                              <div style={{
                                padding: '16px',
                                background: 'white',
                                borderRadius: '6px',
                              }}>
                                <Text variant="bodySm" tone="subdued">Readable PDFs</Text>
                                <Text variant="headingLg" as="p">
                                  {currentJob.totalReadablePdfs || 0}
                                </Text>
                                <Text variant="bodySm" tone="subdued">
                                  {currentJob.readableUsedCached || 0} cached, {currentJob.readableGeneratedNew || 0} new
                                </Text>
                              </div>

                              <div style={{
                                padding: '16px',
                                background: 'white',
                                borderRadius: '6px',
                              }}>
                                <Text variant="bodySm" tone="subdued">Cover PDFs</Text>
                                <Text variant="headingLg" as="p">
                                  {currentJob.totalCovers || 0}
                                </Text>
                                <Text variant="bodySm" tone="subdued">
                                  {currentJob.coversUsedCached || 0} cached, {currentJob.coversGeneratedNew || 0} new
                                </Text>
                              </div>

                              <div style={{
                                padding: '16px',
                                background: 'white',
                                borderRadius: '6px',
                              }}>
                                <Text variant="bodySm" tone="subdued">Montaged PDFs</Text>
                                <Text variant="headingLg" as="p">
                                  {currentJob.totalMontagedPdfs || 0}
                                </Text>
                                <Text variant="bodySm" tone="subdued">
                                  {currentJob.montagedUsedCached || 0} cached, {currentJob.montagedGeneratedNew || 0} new
                                </Text>
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>

                    {currentJob.status === 'completed' && (
                      <div style={{ marginTop: '20px' }}>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                          {verificationLane && currentJob.totalGenerations > 0 && (
                            <Button
                              variant="primary"
                              size="large"
                              onClick={() => navigate(`/printings/${currentJob._id}/verify?lane=${verificationLane}`)}
                            >
                              Verify / Scan
                            </Button>
                          )}
                          {isAll && currentJob.totalBookletPdfs > 0 && (
                            <Button
                              variant="primary"
                              size="large"
                              onClick={() => navigate(`/printings/${currentJob._id}/verify?lane=booklet`)}
                            >
                              Verify Booklets
                            </Button>
                          )}
                          {isAll && currentJob.totalHardCoverGenerations > 0 && (
                            <Button
                              variant="primary"
                              size="large"
                              onClick={() => navigate(`/printings/${currentJob._id}/verify?lane=hard_cover`)}
                            >
                              Verify Hard Covers
                            </Button>
                          )}
                          {currentJob.readableZipUrl && (
                            <Button
                              variant="primary"
                              size="large"
                              onClick={() => window.open(currentJob.readableZipUrl, '_blank')}
                              icon={ExportMinor}
                            >
                              Download Readable PDFs ZIP
                            </Button>
                          )}

                          {currentJob.coversUrl && (
                            <Button
                              size="large"
                              onClick={() => window.open(currentJob.coversUrl, '_blank')}
                              icon={ExportMinor}
                            >
                              Download All Covers PDF
                            </Button>
                          )}

                          {currentJob.montagedZipUrl && (
                            <Button
                              size="large"
                              onClick={() => window.open(currentJob.montagedZipUrl, '_blank')}
                              icon={ExportMinor}
                            >
                              Download Montaged PDFs ZIP
                            </Button>
                          )}

                          {currentJob.bookletZipUrl && (
                            <Button
                              variant="primary"
                              size="large"
                              onClick={() => window.open(currentJob.bookletZipUrl, '_blank')}
                              icon={ExportMinor}
                            >
                              Download Booklet PDFs ZIP
                            </Button>
                          )}
                          {currentJob.hardCoverZipUrl && (
                            <Button
                              size="large"
                              onClick={() => window.open(currentJob.hardCoverZipUrl, '_blank')}
                              icon={ExportMinor}
                            >
                              Download Hard-cover ZIP
                            </Button>
                          )}

                          {/* Backward compat: show old zipUrl for legacy jobs */}
                          {!currentJob.readableZipUrl && !currentJob.coversUrl && currentJob.zipUrl && (
                            <Button
                              variant="primary"
                              size="large"
                              onClick={() => window.open(currentJob.zipUrl, '_blank')}
                              icon={ExportMinor}
                            >
                              Download ZIP (Legacy)
                            </Button>
                          )}

                          {/* Shipping Slip Button */}
                          {currentJob.bosta?.shippingSlipUrl ? (
                            <Button
                              size="large"
                              onClick={() => window.open(currentJob.bosta.shippingSlipUrl, '_blank')}
                              icon={ExportMinor}
                            >
                              Download {shippingSlipLabel} ({shippingProviderLabel})
                            </Button>
                          ) : currentJob.orderReferences && currentJob.orderReferences.length > 0 ? (
                            <Button
                              size="large"
                              onClick={() => handleFetchBostaShippingSlip(currentJob._id)}
                              loading={isFetchingBosta}
                              disabled={isFetchingBosta}
                            >
                              Fetch {shippingSlipLabel} from {shippingProviderLabel}
                            </Button>
                          ) : null}
                        </div>

                        {/* Shipping Info */}
                        {currentJob.bosta && currentJob.orderReferences && currentJob.orderReferences.length > 0 && (
                          <div style={{
                            marginTop: '16px',
                            padding: '12px',
                            background: 'white',
                            borderRadius: '6px',
                            border: '1px solid #e3e3e3'
                          }}>
                            <Text variant="bodySm" as="p" tone="subdued">
                              <strong>{shippingProviderLabel} Shipping:</strong> {currentJob.orderReferences.length} order(s)
                              {currentJob.bosta.trackingNumbers && currentJob.bosta.trackingNumbers.length > 0 && (
                                <> • {currentJob.bosta.trackingNumbers.length} tracking number(s)</>
                              )}
                            </Text>
                            {currentJob.bosta.trackingNumbers && currentJob.bosta.trackingNumbers.length > 0 && (
                              <Text variant="bodySm" as="p" tone="subdued" style={{ marginTop: '4px' }}>
                                Tracking: {currentJob.bosta.trackingNumbers.join(', ')}
                              </Text>
                            )}
                          </div>
                        )}

                        {/* Shipping Error */}
                        {bostaError && (
                          <div style={{ marginTop: '12px' }}>
                            <Banner status="warning">
                              <p>{bostaError}</p>
                            </Banner>
                          </div>
                        )}
                      </div>
                    )}

                    {currentJob.status === 'failed' && currentJob.error && (
                      <Banner status="critical">
                        <p>Error: {currentJob.error}</p>
                      </Banner>
                    )}

                    {currentJob.status === 'completed' && currentJob.totalGenerations === 0 && (
                      <Banner status="info">
                        <p>No generations found with status "approved" for the selected date.</p>
                      </Banner>
                    )}
                  </BlockStack>
                </div>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">
                {isAll ? 'Printing History' : `${profileLabel} Printing History`}
              </Text>

              <TextField
                label="Search"
                labelHidden
                value={searchQuery}
                onChange={(value) => setSearchQuery(value)}
                placeholder="Search by Order Reference (e.g. #1234)"
                prefix={<Icon source={SearchMinor} />}
                autoComplete="off"
                clearButton
                onClearButtonClick={() => setSearchQuery('')}
              />

              {isLoadingHistory ? (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <Spinner size="large" />
                </div>
              ) : history.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                  <Text variant="bodyMd" tone="subdued">
                    No printing jobs found
                  </Text>
                </div>
              ) : (
                <>
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
                    headings={isAll
                      ? ['Date', 'Status', 'Total', 'Booklets', 'Hard Covers', 'Files', 'Downloads', 'Shipping', 'Info']
                      : isBooklet
                      ? ['Date', 'Status', 'Total', 'Booklets', 'Cached', 'Generated', 'Downloads', 'Shipping', 'Info']
                      : ['Date', 'Status', 'Total', 'Readable', 'Covers', 'Montaged', 'Downloads', 'Shipping', 'Info']}
                    rows={history.map((job) => [
                      formatDate(job.date),
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        {getStatusBadge(job.status)}
                        {job.printingProfile === 'premium' && (
                          <Badge status="attention">Premium</Badge>
                        )}
                        {job.printingProfile === 'booklet' && (
                          <Badge status="info">Booklet</Badge>
                        )}
                      </div>,
                      job.totalGenerations || 0,
                      isAll
                        ? (job.totalBookletPdfs || 0)
                        : isBooklet
                        ? (job.totalBookletPdfs || 0)
                        : (job.totalReadablePdfs || job.usedCached + job.generatedNew || 0),
                      isAll
                        ? (
                          job.totalHardCoverGenerations
                          || (job.printingProfile === 'premium' ? job.totalGenerations : 0)
                          || 0
                        )
                        : (isBooklet ? (job.bookletUsedCached || 0) : (job.totalCovers || 0)),
                      isAll
                        ? (
                          (job.totalBookletPdfs || 0)
                          + ((
                            job.totalHardCoverGenerations
                            || (job.printingProfile === 'premium' ? job.totalGenerations : 0)
                            || 0
                          ) * 2)
                        )
                        : (isBooklet ? (job.bookletGeneratedNew || 0) : (job.totalMontagedPdfs || 0)),
                      // Downloads column - readable ZIP + covers PDF + montaged ZIP
                      job.status === 'completed' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {job.readableZipUrl && (
                            <Button plain onClick={() => window.open(job.readableZipUrl, '_blank')}>
                              Readable ZIP
                            </Button>
                          )}
                          {job.coversUrl && (
                            <Button plain onClick={() => window.open(job.coversUrl, '_blank')}>
                              Covers PDF
                            </Button>
                          )}
                          {job.montagedZipUrl && (
                            <Button plain onClick={() => window.open(job.montagedZipUrl, '_blank')}>
                              Montaged ZIP
                            </Button>
                          )}
                          {job.bookletZipUrl && (
                            <Button plain onClick={() => window.open(job.bookletZipUrl, '_blank')}>
                              Booklet ZIP
                            </Button>
                          )}
                          {job.hardCoverZipUrl && (
                            <Button plain onClick={() => window.open(job.hardCoverZipUrl, '_blank')}>
                              Hard-cover ZIP
                            </Button>
                          )}
                          {isAll && job.totalBookletPdfs > 0 && (
                            <Button
                              plain
                              onClick={() => navigate(`/printings/${job._id}/verify?lane=booklet`)}
                            >
                              Verify Booklets
                            </Button>
                          )}
                          {isAll && (
                            job.totalHardCoverGenerations > 0
                            || (
                              job.printingProfile === 'premium'
                              && job.totalGenerations > 0
                            )
                          ) && (
                            <Button
                              plain
                              onClick={() => navigate(`/printings/${job._id}/verify?lane=hard_cover`)}
                            >
                              Verify Hard Covers
                            </Button>
                          )}
                          {verificationLane && job.totalGenerations > 0 && (
                            <Button
                              plain
                              onClick={() => navigate(`/printings/${job._id}/verify?lane=${verificationLane}`)}
                            >
                              Verify / Scan
                            </Button>
                          )}
                          {/* Backward compat: show old zipUrl for legacy jobs */}
                          {!job.bookletZipUrl && !job.hardCoverZipUrl && !job.readableZipUrl && !job.coversUrl && !job.montagedZipUrl && job.zipUrl && (
                            <Button plain onClick={() => window.open(job.zipUrl, '_blank')}>
                              Download ZIP
                            </Button>
                          )}
                          {!job.bookletZipUrl && !job.hardCoverZipUrl && !job.readableZipUrl && !job.coversUrl && !job.montagedZipUrl && !job.zipUrl && (
                            <Text variant="bodySm" tone="subdued">-</Text>
                          )}
                        </div>
                      ) : (
                        <Text variant="bodySm" tone="subdued">-</Text>
                      ),
                      job.status === 'completed' ? (
                        job.bosta?.shippingSlipUrl ? (
                          <Button
                            plain
                            onClick={() => window.open(job.bosta.shippingSlipUrl, '_blank')}
                          >
                            {shippingSlipLabel}
                          </Button>
                        ) : job.orderReferences && job.orderReferences.length > 0 ? (
                          <Button
                            plain
                            onClick={() => handleFetchBostaShippingSlip(job._id)}
                            disabled={isFetchingBosta}
                          >
                            Fetch {shippingSlipLabel}
                          </Button>
                        ) : (
                          <Text variant="bodySm" tone="subdued">No orders</Text>
                        )
                      ) : (
                        <Text variant="bodySm" tone="subdued">-</Text>
                      ),
                      // Info column - enriched generation references
                      (job.generationReferences && job.generationReferences.length > 0) || (job.orderReferences && job.orderReferences.length > 0) ? (
                        <Button
                          icon={InfoMinor}
                          plain
                          onClick={() => handleOpenModal(job)}
                        />
                      ) : (
                        <Text variant="bodySm" tone="subdued">-</Text>
                      ),
                    ])}
                  />

                  {/* Pagination Info */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '16px',
                    padding: '0 4px'
                  }}>
                    <Text variant="bodySm" tone="subdued">
                      Showing {history.length > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0} - {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} items
                    </Text>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <Pagination
                        hasPrevious={currentPage > 1}
                        onPrevious={() => setCurrentPage(currentPage - 1)}
                        hasNext={currentPage < totalPages}
                        onNext={() => setCurrentPage(currentPage + 1)}
                      />
                    )}
                  </div>
                </>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section secondary>
          <Card>
            <BlockStack gap="300">

              <div style={{ marginTop: '16px', padding: '12px', background: '#fef7e0', borderRadius: '6px' }}>
                <Text variant="bodySm" as="p" tone="subdued">
                  <strong>Note:</strong> Generating PDFs may take several minutes depending on the number of generations. The system will cache generated PDFs for future use.
                </Text>
              </div>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
      <Modal
        open={activeModal}
        onClose={handleCloseModal}
        title={<div style={{ padding: '16px' }}>Generation References</div>}
        primaryAction={{
          content: 'Close',
          onAction: handleCloseModal,
        }}
      >
        <Modal.Section>
          <LegacyCard sectioned>
            <div style={{ padding: '0 12px' }}>
              {modalGenerationReferences.length > 0 ? (
                <ul style={{ listStyleType: 'none', paddingLeft: '0', margin: 0 }}>
                  {modalGenerationReferences.map((ref, index) => (
                    <li key={index} style={{ marginBottom: '12px', fontSize: '14px', padding: '8px 0', borderBottom: index < modalGenerationReferences.length - 1 ? '1px solid #e3e3e3' : 'none' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <Text variant="bodyMd" as="span" fontWeight="semibold">
                            {ref.order_id || 'No order'}
                          </Text>
                          <Text variant="bodySm" as="span" tone="subdued">
                            {ref.kid_name} • {ref.story_name}
                          </Text>
                          {ref.story_type && (
                            <Badge status="info">
                              {ref.story_type === 'dynamic_story' ? 'Dynamic Story' : 'Legacy Static'}
                            </Badge>
                          )}
                          {ref.is_premium_printing && (
                            <Badge status="attention">Premium</Badge>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                /* Backward compat: fall back to flat orderReferences for old jobs */
                <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: 0 }}>
                  {modalOrderReferences.map((ref, index) => (
                    <li key={index} style={{ marginBottom: '8px', fontSize: '14px' }}>
                      <Text variant="bodyMd" as="span">{ref.replace('bysnaps:#', '')}</Text>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </LegacyCard>
        </Modal.Section>
      </Modal>
    </>
  );
};

export default PrintingsPage;
